import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./alert-dialog";

const ConfirmDialogComponent = ({ 
  title, 
  description, 
  confirmText = "Confirm", 
  cancelText = "Cancel", 
  variant = "default", 
  onConfirm, 
  onCancel,
  cleanup
}) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Open on mount to trigger animation
    setOpen(true);
  }, []);

  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (!isOpen) {
      // Trigger cancel when clicking outside or pressing Escape
      setTimeout(() => {
        onCancel();
      }, 200); // Allow animation to complete
    }
  };

  const handleConfirmClick = (e) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => {
      onConfirm();
    }, 200);
  };

  const handleCancelClick = (e) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => {
      onCancel();
    }, 200);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className={variant === 'destructive' ? 'text-red-600' : ''}>
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription className="whitespace-pre-line text-base">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="mt-6 gap-2 sm:gap-0">
          <AlertDialogCancel onClick={handleCancelClick}>
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleConfirmClick} 
            className={variant === 'destructive' ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
          >
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const confirmDialog = (options) => {
  return new Promise((resolve) => {
    const div = document.createElement('div');
    document.body.appendChild(div);
    const root = createRoot(div);

    const cleanup = () => {
      setTimeout(() => {
        root.unmount();
        if (div.parentNode) {
          div.parentNode.removeChild(div);
        }
      }, 100);
    };

    const handleConfirm = () => {
      resolve(true);
      cleanup();
    };

    const handleCancel = () => {
      resolve(false);
      cleanup();
    };

    root.render(
      <ConfirmDialogComponent 
        {...options} 
        onConfirm={handleConfirm} 
        onCancel={handleCancel} 
      />
    );
  });
};
